import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import api from "../utils/api";

const API_URL = process.env.REACT_APP_LOCAL_HOST;

export const verifyRecaptcha = createAsyncThunk(
  "auth/verifyRecaptcha",
  async (recaptchaToken, thunkAPI) => {
    try {
      const response = await api.post("/auth/verifyRecaptcha", {
        recaptchaToken,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "reCAPTCHA verification failed"
      );
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async (userData, thunkAPI) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/login`,
        userData,
        { withCredentials: true } // Ensures cookies are sent
      );
      return response.data;
    } catch (error) {
      console.error("Login failed in thunk:", error);

      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async (_, thunkAPI) => {
    try {
      const response = await api.get(`auth/user`, {
        withCredentials: true, // Ensure cookies are included
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Not authenticated"
      );
    }
  }
);

export const updateUserRole = createAsyncThunk(
  "auth/updateUserRole",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `${API_URL}/auth/updateUserRole`,
        payload,
        {
          withCredentials: true, // Ensure cookies are included
        }
      );
      // Refresh the token and wait for it
      await dispatch(refreshToken()).unwrap();

      // Pull updated user information from updated access token
      await dispatch(fetchUser()).unwrap();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Not authenticated");
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `auth/refresh`,
        {},
        { withCredentials: true }
      );
      const { newAccessToken } = response.data;
      // Update Redux manually
      dispatch(setToken(newAccessToken));
      return newAccessToken;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Token refresh failed");
    }
  }
);

// Thunk for handling logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await api.post(`${API_URL}/auth/logout`, { withCredentials: true });
      return true;
    } catch (error) {
      console.error("Error: ", error);
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

const initialState = {
  user: null,
  accessToken: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.accessToken = action.payload;
    },
    resetRecaptcha: (state) => {
      state.verified = false;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //Handle reCAPTCHA verification
      .addCase(verifyRecaptcha.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyRecaptcha.fulfilled, (state) => {
        state.isLoading = false;
        state.verified = true;
      })
      .addCase(verifyRecaptcha.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Handle loginUser actions
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.token;
        state.user = action.payload.user;
        state.error = null;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Login Failed";
      })
      // Handle fetchUser actions
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isLoading = false;
        state.isAuthenticated = true;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        // state.accessToken = null;
        state.error = action.payload;
      })
      // Handle logout actions
      .addCase(logoutUser.fulfilled, () => initialState)
      .addCase(logoutUser.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload || "Logout failed";
      })
      // Handle token refresh
      .addCase(refreshToken.fulfilled, (state, action) => {
        // state.accessToken = action.payload;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload;
        state.accessToken = null;
        state.isAuthenticated = false;
      });
  },
});

export const { setToken, resetRecaptcha } = authSlice.actions;
export const authReducer = authSlice.reducer;
